import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModal } from '../context/ModalContext';
import api from '../utils/axiosInstance';
import { Card, CardBody, Spinner } from '../components/ui';
import { FaWhatsapp, FaEnvelope } from 'react-icons/fa';

const RESEND_COOLDOWN_SEC = 30;

export default function VerifyOtp() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState('');
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  const email = useLocation().state?.email;
  const modal = useModal();

  // Auto-focus first input on mount so users can type immediately
  useEffect(() => {
    inputsRef.current[0]?.focus();
    // Start a short cooldown so users don't spam resend the moment they land
    setResendIn(15);
  }, []);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submitOtp = async (codeStr) => {
    if (codeStr.length !== 6) return;
    setError('');
    setVerifying(true);
    try {
      const res = await api.post('/api/auth/verify-otp', { email, otp: codeStr });
      if (res.data.success) {
        // GA4 conversion event — fires on completed signup (email + OTP verified)
        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'sign_up_completed',
            method: 'email_otp',
          });
        }
        await modal.alert('Verified! Redirecting to login...', 'success');
        let loginPath = '/login';
        try {
          const next = sessionStorage.getItem('walytic_post_auth_next');
          if (next && next.startsWith('/') && !next.startsWith('//')) {
            loginPath = `/login?next=${encodeURIComponent(next)}`;
          }
        } catch {
          // ignore
        }
        navigate(loginPath);
      } else {
        setError(res.data.message || 'Invalid code. Try again.');
        setOtp(['', '', '', '', '', '']);
        inputsRef.current[0]?.focus();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed. Try again.');
      setOtp(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (e, i) => {
    const value = e.target.value.replace(/\D/, '');
    if (!value) return;
    const newOtp = [...otp];
    newOtp[i] = value[0];
    setOtp(newOtp);
    setError('');
    // Auto-advance to next input
    if (i < 5) inputsRef.current[i + 1]?.focus();
    // Auto-submit when all 6 digits entered
    const code = newOtp.join('');
    if (code.length === 6) submitOtp(code);
  };

  const handleKeyDown = (e, i) => {
    if (e.key === 'Backspace') {
      if (otp[i]) {
        const newOtp = [...otp];
        newOtp[i] = '';
        setOtp(newOtp);
      } else if (i > 0) {
        inputsRef.current[i - 1]?.focus();
        const newOtp = [...otp];
        newOtp[i - 1] = '';
        setOtp(newOtp);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      inputsRef.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) newOtp[i] = pasted[i] || '';
    setOtp(newOtp);
    const focusIdx = Math.min(pasted.length, 5);
    inputsRef.current[focusIdx]?.focus();
    if (pasted.length === 6) submitOtp(pasted);
  };

  const handleVerify = () => submitOtp(otp.join(''));

  const resendOtp = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      await api.post('/api/auth/resend-otp', { email });
      setResendIn(RESEND_COOLDOWN_SEC);
      modal.alert('New code sent. Check your inbox (and spam folder).', 'success');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not resend. Try again in a minute.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page flex min-h-screen items-center justify-center p-4">
      <Card className="auth-shell w-full max-w-sm">
        <CardBody className="py-8 px-6 sm:py-10 sm:px-8">
          <div className="flex justify-center mb-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-brand">
              <FaWhatsapp size={22} color="#fff" />
            </div>
          </div>

          <h1 className="text-xl font-bold text-ink-primary text-center mb-1">Verify Your Email</h1>
          <p className="text-sm text-ink-muted text-center mb-6">
            We sent a 6-digit code to
          </p>

          <div className="mb-1 flex items-center justify-center gap-2 rounded-md bg-surface-bg p-2.5">
            <FaEnvelope size={13} className="text-ink-muted" />
            <span className="text-sm font-medium text-ink-primary truncate">{email || 'your email'}</span>
          </div>
          <p className="text-xs text-ink-muted text-center mb-5">
            Wrong email?{' '}
            <button onClick={() => navigate('/signup')} className="text-brand hover:underline bg-transparent border-0 p-0 cursor-pointer">
              Go back
            </button>
          </p>

          <div className="flex justify-center gap-2 sm:gap-2.5 mb-3" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                disabled={verifying}
                className="h-12 w-10 rounded-md border border-surface-border bg-surface-card text-center text-xl font-bold text-ink-primary outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:opacity-60 sm:w-11"
              />
            ))}
          </div>

          {error && (
            <p className="text-xs text-red-500 text-center mb-3">{error}</p>
          )}

          <button
            onClick={handleVerify}
            className="btn-primary w-full mb-3"
            disabled={verifying || otp.join('').length !== 6}
          >
            {verifying ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size="sm" /> Verifying...
              </span>
            ) : (
              'Verify'
            )}
          </button>

          <p className="text-center text-sm text-ink-muted">
            Didn't get the code?{' '}
            <button
              onClick={resendOtp}
              disabled={resending || resendIn > 0}
              className="text-brand hover:text-brand-700 font-medium bg-transparent border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resending ? 'Sending...' : resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
            </button>
          </p>
          <p className="text-xs text-ink-muted text-center mt-3">
            Tip: emails sometimes land in spam. Mark them as Not Spam so future ones reach your inbox.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
